/**
 * Phase 9.7.21 — 🌟 Spotlight Sundays page
 * Route: /spotlight
 *
 * Weekly winners page: explains the program + surfaces current spotlight
 * winners + past spotlight history. Powered by existing endpoints:
 *   GET /api/discover/spotlight-winners — current active spotlight tracks
 *   GET /api/discover/most-pushed-today — momentum proof point
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import SharedNavigation from "@/components/SharedNavigation";
import { useCurrentUser } from "@/lib/useCurrentUser";
import { optimizeImage } from "@/lib/mediaUrls";

const fmt = (n) => {
  const v = Math.round(Number(n) || 0);
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return String(v);
};

const REWARDS = [
  { tier: "🥇 #1 Spotlight Winner", perks: [
    "Top hero placement for the week",
    "Boosted reach pool — 25K minimum",
    "Featured social share card from RGM",
    "Front-of-queue A&R review next cycle",
  ]},
  { tier: "🥈 #2 Spotlight Winner", perks: [
    "Featured Push Today rail (homepage)",
    "Boosted reach pool — 15K minimum",
    "🏆 Most Pushed leaderboard priority",
  ]},
  { tier: "🥉 #3 Spotlight Winner", perks: [
    "Featured Push Today rail (homepage)",
    "Boosted reach pool — 10K minimum",
    "Spotlight badge on artist profile",
  ]},
];

const SpotlightPage = () => {
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useCurrentUser();

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/discover/spotlight-winners");
        setWinners(Array.isArray(r.data) ? r.data : (r.data?.winners || r.data || []));
      } catch {
        setWinners([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const top3 = winners.slice(0, 3);

  return (
    <div className="min-h-screen bg-black text-white">
      <SharedNavigation currentUser={currentUser} />

      {/* Hero */}
      <header className="max-w-5xl mx-auto px-4 sm:px-6 pt-24 sm:pt-28 pb-10 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/20 border border-purple-400/40 text-purple-200 text-[10px] uppercase tracking-[3px] font-black">
          🌟 Spotlight Sundays
        </div>
        <h1 className="mt-4 text-4xl sm:text-6xl font-black bg-gradient-to-r from-amber-300 via-pink-300 to-purple-300 bg-clip-text text-transparent">
          Weekly Winners.
          <br />
          Real Push.
        </h1>
        <p className="mt-4 text-amber-100/80 max-w-2xl mx-auto text-sm sm:text-base">
          Every Sunday, RGM Studio crowns its <strong>Top 3 most undeniable artists</strong>
          across the platform — graded on submission strength, engagement,
          and momentum. Winners get a real promotional boost, a placement
          card, and a permanent spotlight on their profile.
        </p>
        <div className="mt-6 flex justify-center gap-3 flex-wrap">
          <Link
            to="/submit"
            className="px-5 py-3 rounded-full bg-gradient-to-r from-amber-500 to-pink-500 hover:from-amber-400 hover:to-pink-400 text-white font-bold shadow-lg shadow-amber-500/30"
            data-testid="spotlight-cta-submit"
          >
            🎤 Submit for next Sunday
          </Link>
          <Link
            to="/leaderboard/today"
            className="px-5 py-3 rounded-full bg-white/5 border border-amber-400/40 text-amber-100 hover:bg-white/10 font-bold"
            data-testid="spotlight-cta-leaderboard"
          >
            🏆 See today's leaderboard
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 pb-20 space-y-12">
        {/* Current winners */}
        <section data-testid="spotlight-current-winners">
          <h2 className="text-xl sm:text-2xl font-black text-white">This week's winners</h2>
          {loading && <p className="text-sm text-amber-200/60 mt-3">Loading current spotlight…</p>}
          {!loading && top3.length === 0 && (
            <div className="mt-4 p-8 rounded-2xl bg-gradient-to-br from-purple-900/15 to-black border border-purple-500/20 text-center">
              <div className="text-5xl mb-3">🌙</div>
              <p className="text-white font-bold">No active spotlight winners yet.</p>
              <p className="text-sm text-amber-200/70 mt-1">
                The next Spotlight Sunday goes live this weekend. Submit your music
                to be considered.
              </p>
            </div>
          )}
          {!loading && top3.length > 0 && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {top3.map((w, i) => {
                const t = w.top_track || w;
                const reach = (Number(t.boost_applied || 0) + Number(t.floor_applied || 0)) || Number(w.total_reach || 0);
                const medal = ["🥇", "🥈", "🥉"][i] || "🌟";
                return (
                  <Link
                    key={w.user_id || t.submission_id || i}
                    to={t.submission_id ? `/track/${t.submission_id}` : `/artist/${w.user_id || ""}`}
                    className="block rounded-2xl border border-purple-500/30 bg-gradient-to-b from-purple-900/20 via-black to-black overflow-hidden hover:border-amber-400/50 transition-colors"
                    data-testid={`spotlight-winner-${i + 1}`}
                  >
                    <div className="relative aspect-square">
                      {(t.cover_url || t.cover_art) ? (
                        <img src={optimizeImage(t.cover_url || t.cover_art, 'card')} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-purple-700 to-amber-600 flex items-center justify-center text-6xl">🎶</div>
                      )}
                      <div className="absolute top-3 left-3 text-4xl drop-shadow-lg">{medal}</div>
                      <div className="absolute bottom-3 left-3 right-3">
                        <p className="text-white font-bold truncate">{t.track_title || w.track_title || "Untitled"}</p>
                        <p className="text-pink-200 text-xs truncate">
                          {w.artist_name || t.display_artist_name || t.artist_name || "RGM Artist"}
                        </p>
                      </div>
                    </div>
                    <div className="p-3 flex items-center gap-3 text-[11px]">
                      {reach > 0 && (
                        <span className="text-amber-300">🏆 {fmt(reach)} reach</span>
                      )}
                      <span className="text-purple-300 font-bold uppercase tracking-wider text-[10px]">
                        🌟 Spotlight #{i + 1}
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </section>

        {/* Rewards */}
        <section data-testid="spotlight-rewards">
          <h2 className="text-xl sm:text-2xl font-black text-white">What you get</h2>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            {REWARDS.map((r) => (
              <div key={r.tier} className="rounded-2xl border border-amber-400/30 bg-gradient-to-br from-amber-500/5 to-black p-5">
                <p className="text-amber-200 font-black text-base">{r.tier}</p>
                <ul className="mt-3 space-y-2 text-sm text-amber-100/80">
                  {r.perks.map((p) => (
                    <li key={p} className="flex gap-2"><span className="text-amber-300">✓</span><span>{p}</span></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* How it works */}
        <section data-testid="spotlight-how-it-works">
          <h2 className="text-xl sm:text-2xl font-black text-white">How Spotlight Sundays work</h2>
          <ol className="mt-4 space-y-3 text-sm text-amber-100/80 list-decimal list-inside">
            <li>Submit your track to RGM Studio any time during the week.</li>
            <li>The A&R team reviews + scores submissions on craft, mix, vocal clarity, and replay value.</li>
            <li>Engagement, gifts, and reactions from the community count toward your final score.</li>
            <li>Every Sunday at 8pm EST the Top 3 are announced + given a real promotional push.</li>
            <li>Winners stay on the homepage Featured Push Today rail and Boost Pulse all week.</li>
          </ol>
        </section>

        <section className="text-center">
          <Link
            to="/submit"
            className="inline-block px-8 py-4 rounded-full bg-gradient-to-r from-amber-500 via-pink-500 to-purple-500 text-white font-black text-lg shadow-xl shadow-amber-500/40 hover:scale-[1.02] transition-transform"
            data-testid="spotlight-bottom-cta"
          >
            🎤 Submit your music
          </Link>
        </section>
      </main>
    </div>
  );
};

export default SpotlightPage;
